import React, { useState, useEffect } from 'react';
import { BarChart3, Users, CheckCircle, TrendingUp, Activity, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL || '';

function StatCard({ icon: Icon, label, value, sub, color = 'stone' }) {
    const colors = {
        stone: 'bg-stone-50 text-stone-700',
        emerald: 'bg-emerald-50 text-emerald-700',
        amber: 'bg-amber-50 text-amber-700',
        blue: 'bg-blue-50 text-blue-700',
    };
    return (
        <div className={`rounded-lg p-3 ${colors[color]}`}>
            <div className="flex items-center gap-1.5 mb-1">
                <Icon className="w-3.5 h-3.5 opacity-60" />
                <span className="text-[10px] font-medium uppercase tracking-wide opacity-60">{label}</span>
            </div>
            <div className="text-lg font-bold">{value}</div>
            {sub && <div className="text-[10px] opacity-50">{sub}</div>}
        </div>
    );
}

function FunnelBar({ label, count, total, type }) {
    const pct = total ? Math.round((count / total) * 100) : 0;
    const typeColors = {
        email: 'bg-blue-400', wait: 'bg-stone-300', if_else: 'bg-amber-400', branch: 'bg-amber-400',
        ab_split: 'bg-purple-400', tag_update: 'bg-teal-400', task: 'bg-indigo-400',
        notification: 'bg-orange-400', end: 'bg-emerald-400', goal: 'bg-rose-400',
        webhook_call: 'bg-cyan-400', update_field: 'bg-slate-400', assign_user: 'bg-violet-400',
        default: 'bg-stone-400',
    };
    const barColor = typeColors[type] || typeColors.default;

    return (
        <div className="flex items-center gap-2 text-xs">
            <div className="w-28 truncate text-stone-600 text-right" title={label}>{label}</div>
            <div className="flex-1 h-5 bg-stone-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${barColor} transition-all`}
                    style={{ width: `${Math.max(pct, 2)}%` }} />
            </div>
            <div className="w-16 text-stone-500 text-right">{count} <span className="text-stone-400">({pct}%)</span></div>
        </div>
    );
}

export default function CampaignAnalytics({ campaignId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(true);

    useEffect(() => {
        if (!campaignId) return;
        const token = localStorage.getItem('crm_token');
        fetch(`${API}/api/campaigns/${campaignId}/analytics`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => r.json())
            .then(setData)
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [campaignId]);

    if (loading) return <div className="p-4 text-xs text-stone-400">Loading analytics...</div>;
    if (!data) return null;

    const { summary, step_metrics, ab_results } = data;

    return (
        <div className="bg-white border-b border-stone-200">
            <button onClick={() => setExpanded(!expanded)}
                className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-stone-50 transition-colors">
                <div className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium text-stone-700">Analytics</span>
                </div>
                {expanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
            </button>

            {expanded && (
                <div className="px-4 pb-4 space-y-4">
                    {/* Summary cards */}
                    <div className="grid grid-cols-2 gap-2">
                        <StatCard icon={Users} label="Enrolled" value={summary.total_enrolled} color="stone" />
                        <StatCard icon={Activity} label="Active" value={summary.active} color="blue" />
                        <StatCard icon={CheckCircle} label="Completed" value={summary.completed} color="emerald" />
                        <StatCard icon={TrendingUp} label="Conversion" value={`${summary.conversion_rate}%`}
                            sub={`${summary.converted} converted`} color="amber" />
                    </div>

                    {/* Step funnel */}
                    {step_metrics && step_metrics.length > 0 && (
                        <div className="space-y-1.5">
                            <h4 className="text-[10px] font-semibold text-stone-500 uppercase tracking-wider">Step Funnel</h4>
                            {step_metrics.map(m => (
                                <FunnelBar key={m.step_id} label={m.label || m.step_type}
                                    count={m.entered} total={summary.total_enrolled} type={m.step_type} />
                            ))}
                        </div>
                    )}

                    {/* A/B Results */}
                    {ab_results && ab_results.group_a && (
                        <div className="bg-purple-50 rounded-lg p-3 space-y-2">
                            <h4 className="text-[10px] font-semibold text-purple-600 uppercase tracking-wider">A/B Test Results</h4>
                            <div className="grid grid-cols-2 gap-3 text-xs">
                                <div>
                                    <div className="font-medium text-stone-700">Path A</div>
                                    <div className="text-stone-500">{ab_results.group_a.count} contacts</div>
                                    <div className="text-purple-600 font-semibold">{ab_results.group_a.rate}% converted</div>
                                </div>
                                <div>
                                    <div className="font-medium text-stone-700">Path B</div>
                                    <div className="text-stone-500">{ab_results.group_b.count} contacts</div>
                                    <div className="text-purple-600 font-semibold">{ab_results.group_b.rate}% converted</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
